// ═══════════════════════════════════════════════════════════════════════════
// Guardado de PDF compatible con iOS
//
// PROBLEMA REAL (reportado desde un iPhone): tras generar una remisión, al
// recargar Safari mostraba
//     "WebKitBlobResource error 1"
//
// CAUSA: iOS no soporta bien el atributo `download`, así que `doc.save()` de
// jsPDF acaba NAVEGANDO la pestaña actual a una URL `blob:`. La app deja de
// estar en su propia dirección. Al recargar, Safari intenta volver a pedir ese
// blob —ya revocado— y falla. Se "arregla solo" al navegar de vuelta, que es
// justo lo que describió el operador.
//
// SOLUCIÓN: en iOS se abre el PDF en una pestaña NUEVA, para que la pestaña de
// la app conserve su dirección. En el resto se descarga como siempre.
//
// La revocación se retrasa: revocar de inmediato corta la descarga en Safari
// antes de que termine de leer el blob.
// ═══════════════════════════════════════════════════════════════════════════

const REVOCAR_TRAS = 60000;   // 1 min: de sobra para que el navegador lea el blob

// ── Compartir, que en un teléfono es lo que "descargar" quiere decir ──────
//
// El operador, desde un iPhone: "no puedo descargar el pdf. Me interesa poder
// mandarlo por whatsapp o por correo electrónico desde la app. Abre el url de
// app; pero no veo donde lo guarde en el iphone."
//
// Tenía razón en el diagnóstico sin saberlo: en iOS **no se puede descargar**
// así. `doc.save()` de jsPDF crea un <a download> y Safari IGNORA ese
// atributo — abre el PDF y ahí acaba todo. No hay carpeta de descargas que
// mirar porque nunca se descargó nada.
//
// Lo que iOS sí tiene es la hoja de compartir, y es mejor que una descarga:
// desde ahí sale WhatsApp, Correo y «Guardar en Archivos» — las tres cosas que
// pidió, sin construir ninguna.
//
// `canCompartirArchivo` se pregunta ANTES de enseñar el botón: en un escritorio
// sin la API, ofrecer "Compartir" sería ofrecer un botón que no hace nada.
export function canCompartirArchivo(nombre = "documento.pdf", tipo = tipoPorNombre(nombre)) {
  try {
    if (typeof navigator === "undefined" || !navigator.canShare || !navigator.share) return false;
    // Un File de cero bytes basta para preguntar: no se comparte, solo se
    // consulta si el navegador aceptaría archivos de este tipo.
    //
    // Y se pregunta por EL TIPO DE VERDAD. Antes se preguntaba siempre por un
    // PDF, así que para un XML la respuesta era "sí" y al tocar Compartir la
    // hoja no abría: en el teléfono el XML se iba a una pestaña nueva, y lo
    // único compartible desde ahí era la dirección de esa pestaña.
    return navigator.canShare({ files: [new File([], nombre, { type: tipo })] });
  } catch { return false; }
}

/**
 * El tipo MIME que le toca a un nombre de archivo. La hoja de compartir decide
 * por el tipo (y por la extensión) qué apps ofrecer; un XML declarado como PDF
 * confunde a las dos partes.
 */
export function tipoPorNombre(nombre = "") {
  const ext = String(nombre).toLowerCase().split(".").pop();
  return { pdf: "application/pdf", xml: "text/xml", json: "application/json",
           txt: "text/plain", csv: "text/csv" }[ext] || "application/octet-stream";
}

/**
 * Abre la hoja de compartir con el PDF.
 * @returns true si se compartió, false si no se pudo (quien llama decide).
 *
 * Cancelar NO es un fallo: si la persona cierra la hoja, `share` rechaza con
 * AbortError y aquí se trata como "ya está", sin avisos de error por algo que
 * hizo a propósito.
 */
export async function compartirArchivo(blob, nombre) {
  return compartirArchivos([{ blob, nombre }], nombre);
}

/**
 * Abre la hoja de compartir con VARIOS archivos a la vez.
 *
 * El operador: "los clientes piden el pdf y el xml para sus archivos; se
 * deberían poder compartir desde el teléfono con un solo click también."
 *
 * Un solo toque y los dos van juntos en el mismo WhatsApp o el mismo correo:
 * es lo que el cliente espera recibir, y mandarlos por separado era mandar dos
 * mensajes y acordarse del segundo.
 *
 * El tipo de cada archivo se declara por su NOMBRE, no por lo que diga el blob:
 * un servidor puede entregar el XML como `application/octet-stream`, y con eso
 * la hoja no sabe qué hacer.
 */
export async function compartirArchivos(archivos = [], titulo = "") {
  const lista = archivos.filter(a => a?.blob && a?.nombre);
  if (!lista.length) return false;
  try {
    if (typeof navigator === "undefined" || !navigator.canShare || !navigator.share) return false;
    const files = lista.map(a => new File([a.blob], a.nombre, { type: tipoPorNombre(a.nombre) }));
    if (!navigator.canShare({ files })) return false;
    await navigator.share({ files, title: titulo || lista[0].nombre });
    return true;
  } catch (e) {
    if (e?.name === "AbortError") return true;   // lo cerró quien decide
    console.warn("[pdf] no se pudo compartir:", e?.message);
    return false;
  }
}

/**
 * Un archivo que NO se mira: se comparte si el aparato sabe, y si no, se
 * descarga. Sin visor y sin pestaña nueva.
 *
 * Es el camino del XML. El operador, desde el iPhone: "el xml no se puede
 * compartir directamente en el teléfono; lo que se puede compartir es un url
 * de la página, la que hace el rendering. No debería tener vista previa, solo
 * botón de compartir."
 *
 * Tenía razón en lo que veía: el XML se abría en una pestaña nueva —eso era
 * la "página que hace el rendering"— y desde ahí Safari solo ofrecía
 * compartir la dirección `blob:` de esa pestaña, que no le sirve a nadie. Un
 * XML no es para leerse en pantalla; es para mandarse al contador.
 *
 * En iPhone la hoja acepta cualquier tipo de archivo (WebKit no filtra), así
 * que el XML sale directo. En Android, Chrome no admite `.xml` en la hoja y
 * ahí sí se descarga, que en Android funciona.
 */
export async function compartirOdescargar(blob, nombre) {
  if (canCompartirArchivo(nombre) && await compartirArchivo(blob, nombre)) return "compartido";
  descargarArchivo(blob, nombre);
  return "descargado";
}

// ── Ver antes de guardar ───────────────────────────────────────────────────
//
// El operador: "es mucho más cómodo que cada remisión, factura, remisión de
// envío se vea en modo preview en lugar de que se descargue, luego haya que
// buscarla y abrirla. Mejor que del preview se pueda descargar."
//
// Tiene razón, y el costo del camino viejo se nota sobre todo cuando algo sale
// mal: para descubrir que una remisión tenía el cliente equivocado había que
// descargarla, encontrarla en la carpeta de Descargas, abrirla, cerrarla y
// borrarla. Cuatro pasos para mirar.
//
// Se resuelve aquí, en el único sitio por el que pasan TODOS los documentos,
// en vez de tocar cada generador. Quien quiera visor pone el gancho; si nadie
// lo pone, se descarga como siempre — así el cambio no puede romper un camino
// que no se haya revisado.
let alVerDocumento = null;

/** La app instala aquí su visor. Sin gancho, todo sigue descargándose. */
export function instalarVisor(fn) { alVerDocumento = fn; }

/** ¿Hay visor? Para que quien llame decida si ofrece "ver" o "descargar". */
export const hayVisor = () => typeof alVerDocumento === "function";

/** iPhone/iPad, incluido el iPad que se anuncia como Mac con pantalla táctil. */
export function esIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) ||
         (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
}

/**
 * Guarda el documento sin dejar la app en una URL `blob:`.
 * @param doc     documento de jsPDF
 * @param nombre  nombre de archivo sugerido
 */
export function guardarPDF(doc, nombre, { forzarDescarga = false } = {}) {
  // Con visor instalado se MUESTRA, y desde ahí se descarga. En iOS eso además
  // resuelve el problema de siempre: la pestaña de la app nunca navega a una
  // URL blob:, así que no hay "WebKitBlobResource error 1" al recargar.
  if (!forzarDescarga && alVerDocumento) {
    try {
      // El BLOB además de la URL. Sin él no se puede compartir: la hoja nativa
      // recibe un File, no una dirección, y de una `blob:` no se recupera el
      // contenido sin volver a pedirlo por red.
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      alVerDocumento({
        url, nombre, tipo: "application/pdf", blob,
        descargar: () => doc.save(nombre),
        // Antes se usaba `doc.output("bloburl")`, que crea la URL y no la
        // suelta nunca: medio mega por cada documento visto, acumulándose en
        // un teléfono. Ahora se crea aquí y se libera al cerrar.
        //
        // Pero CON RETRASO, y por la misma razón que lo tiene `descargarArchivo`:
        // «Abrir aparte» deja esta URL viva en otra pestaña. Revocarla en el
        // instante de cerrar el visor rompería esa pestaña al recargarla — se
        // arreglaría un derroche creando un defecto peor, y encima uno que
        // solo aparece cuando alguien hizo justo lo que le ofrecimos.
        alCerrar: () => setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch { /* ya liberado */ }
        }, REVOCAR_TRAS),
      });
      return;
    } catch (e) {
      console.warn("[pdf] el visor falló, se descarga:", e?.message);
    }
  }

  if (!esIOS()) {
    doc.save(nombre);          // camino de siempre en escritorio y Android
    return;
  }

  try {
    const url = doc.output("bloburl");
    // Pestaña nueva: la de la app conserva su dirección y sigue recargable.
    const abierta = window.open(url, "_blank");
    if (!abierta) {
      // Bloqueador de ventanas emergentes: se cae al camino normal, que al
      // menos entrega el archivo aunque deje la pestaña en blob:.
      doc.save(nombre);
      return;
    }
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ya liberado */ } }, REVOCAR_TRAS);
  } catch (e) {
    console.warn("[pdf] no se pudo abrir en pestaña nueva, se descarga:", e?.message);
    doc.save(nombre);
  }
}

/**
 * Un PDF con sus compañeros —el XML de un CFDI—, al visor. Desde ahí se
 * comparten o se descargan TODOS juntos.
 *
 * Existe por un defecto que se veía como "el XML se comparte como URL". El
 * botón XML traía el archivo del servidor y DESPUÉS llamaba a la hoja de
 * compartir. Safari solo abre la hoja dentro de un toque reciente; tras la
 * espera de la red el toque ya caducó, la hoja se niega y el respaldo abría una
 * pestaña `blob:`, que es lo único que Safari sabe compartir de una pestaña.
 * El PDF nunca falló porque se comparte desde el visor, con un toque nuevo.
 *
 * Así que la red va primero y la hoja después, en un toque propio dentro del
 * visor. Sin visor, se hace lo que se puede: compartir y, si no, descargar
 * cada uno.
 *
 * @param principal  { blob, nombre } — el que se ve, un PDF
 * @param adjuntos   [{ blob, nombre }] — los que viajan con él
 */
export async function verConAdjuntos(principal, adjuntos = [], titulo = "") {
  const todos = [principal, ...adjuntos].filter(a => a?.blob && a?.nombre);
  if (alVerDocumento) {
    const url = URL.createObjectURL(principal.blob);
    try {
      alVerDocumento({
        url, nombre: principal.nombre, tipo: "application/pdf", blob: principal.blob,
        adjuntos: todos.slice(1), titulo,
        descargar: () => descargarArchivo(principal.blob, principal.nombre),
        descargarTodos: () => todos.forEach(a => descargarArchivo(a.blob, a.nombre)),
        alCerrar: () => { try { URL.revokeObjectURL(url); } catch { /* ya */ } },
      });
      return;
    } catch (e) {
      try { URL.revokeObjectURL(url); } catch { /* ya */ }
      console.warn("[pdf] el visor falló:", e?.message);
    }
  }
  if (await compartirArchivos(todos, titulo)) return;
  todos.forEach(a => descargarArchivo(a.blob, a.nombre));
}

/**
 * Descarga un archivo cualquiera (JSON, XML) sin romper la pestaña en iOS.
 * Misma lógica: revocación retrasada, y en iOS pestaña nueva.
 */
export function verOdescargar(blob, nombre) {
  if (alVerDocumento) {
    const url = URL.createObjectURL(blob);
    try {
      alVerDocumento({ url, nombre, tipo: blob.type || "", blob,
                       descargar: () => descargarArchivo(blob, nombre),
                       alCerrar: () => { try { URL.revokeObjectURL(url); } catch { /* ya */ } } });
      return;
    } catch (e) {
      try { URL.revokeObjectURL(url); } catch { /* ya */ }
      console.warn("[pdf] el visor falló, se descarga:", e?.message);
    }
  }
  descargarArchivo(blob, nombre);
}

export function descargarArchivo(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const liberar = () => setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch { /* ya liberado */ }
  }, REVOCAR_TRAS);

  if (esIOS()) {
    const abierta = window.open(url, "_blank");
    if (abierta) { liberar(); return; }
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  liberar();
}
